function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { cloneElement } from 'react';
import { Item } from '@adobe/react-spectrum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dh as dhIcons } from '@deephaven/icons';
import { isElementOfType } from '@deephaven/react-hooks';
import { ensureArray, NON_BREAKING_SPACE } from '@deephaven/utils';
import { getItemTextValue, isItemElement, isSectionElement, ITEM_EMPTY_STRING_TEXT_VALUE } from "./itemUtils.js";
import { ItemContent } from "../ItemContent.js";
import { Icon } from "../icons.js";
import { Text } from "../Text.js";
/**
 * If the given content is a string, wrap it in an Icon component. Otherwise,
 * return the original content. If the key is not found in the dhIcons object,
 * the vsBlank icon will be used.
 * @param maybeIconKey The content to wrap
 * @param slot The slot to use for the Icon component
 * @returns The wrapped content or original content if not a string
 */
import { jsx as _jsx } from "react/jsx-runtime";
export function wrapIcon(maybeIconKey, slot) {
  var _dhIcons$maybeIconKey;
  // eslint-disable-next-line no-param-reassign
  maybeIconKey = maybeIconKey !== null && maybeIconKey !== void 0 ? maybeIconKey : '';
  if (typeof maybeIconKey !== 'string') {
    return maybeIconKey;
  }
  return /*#__PURE__*/_jsx(Icon, {
    slot: slot,
    children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
      icon: (_dhIcons$maybeIconKey = dhIcons[maybeIconKey]) !== null && _dhIcons$maybeIconKey !== void 0 ? _dhIcons$maybeIconKey : dhIcons.vsBlank
    })
  });
}

/**
 * Ensure all primitive children are wrapped in `Item` elements and that all
 * `Item` element content is wrapped in `ItemContent` elements to handle text
 * overflow consistently and to support tooltips.
 * @param itemsOrSections The items or sections to wrap
 * @param tooltipOptions The tooltip options to use when wrapping items
 * @returns The wrapped items or sections
 */
export function wrapItemChildren(itemsOrSections, tooltipOptions) {
  if (itemsOrSections == null) {
    return [];
  }
  var itemsOrSectionsArray = ensureArray(itemsOrSections);
  var result = itemsOrSectionsArray.map(item => {
    if (isItemElement(item)) {
      var _item$key;
      // Item content is already wrapped
      if (isElementOfType(item.props.children, ItemContent)) {
        return item;
      }
      var key = (_item$key = item.key) !== null && _item$key !== void 0 ? _item$key : item.props.textValue;
      var _textValue = getItemTextValue(item);

      // Wrap in `ItemContent` so we can support tooltips and handle text
      // overflow
      return /*#__PURE__*/cloneElement(item, _objectSpread(_objectSpread({}, item.props), {}, {
        key,
        textValue: _textValue,
        children: /*#__PURE__*/_jsx(ItemContent, {
          tooltipOptions: tooltipOptions,
          children: item.props.children
        })
      }));
    }
    if (isSectionElement(item)) {
      var _item$key2;
      return /*#__PURE__*/cloneElement(item, _objectSpread(_objectSpread({}, item.props), {}, {
        key: (_item$key2 = item.key) !== null && _item$key2 !== void 0 ? _item$key2 : typeof item.props.title === 'string' ? item.props.title : undefined,
        children: wrapItemChildren(item.props.children, tooltipOptions)
      }));
    }
    var text = String(item);
    var textValue = text === '' ? ITEM_EMPTY_STRING_TEXT_VALUE : text;
    return /*#__PURE__*/_jsx(Item, {
      textValue: textValue,
      children: /*#__PURE__*/_jsx(ItemContent, {
        tooltipOptions: tooltipOptions,
        children: text
      })
    }, text);
  });

  // Keep consistent with original data structure as array or single node
  return Array.isArray(itemsOrSections) ? result : result[0];
}

/**
 * If the given content is a primitive type, wrap it in a Text component.
 * @param content The content to wrap
 * @param slot The slot to use for the Text component
 * @returns The wrapped content or original content if not a primitive type
 */
export function wrapPrimitiveWithText(content, slot) {
  // eslint-disable-next-line no-param-reassign
  content = content !== null && content !== void 0 ? content : '';
  if (['string', 'boolean', 'number'].includes(typeof content)) {
    return /*#__PURE__*/_jsx(Text, {
      slot: slot,
      children: content === '' ? NON_BREAKING_SPACE : String(content)
    });
  }
  return content;
}
//# sourceMappingURL=itemWrapperUtils.js.map